import { useAppSelector } from '../app/hooks';
import {
  selectButtonStyle,
  selectMainColor,
} from '../features/appconfig/store/selectors';

export const useButtonColors = () => {
  const mainColor = useAppSelector(selectMainColor);
  const buttonStyle = useAppSelector(selectButtonStyle);
  const secondaryColor = getSecondaryColor(mainColor);
  const buttonColors = { mainColor, secondaryColor };

  const backgroundColor =
    buttonStyle === 'primary' ? buttonColors.mainColor : buttonColors.secondaryColor;

  const gradientBackgroundColours =
    // gradient initilalized with a single color
    // prepared for future addition of a secondary color.
    buttonStyle === 'primary'
      ? [buttonColors.mainColor, buttonColors.mainColor]
      : [buttonColors.secondaryColor, buttonColors.secondaryColor];

  const iconOrTextColor = buttonStyle === 'primary' ? '#FFFFFF' : buttonColors.mainColor;

  return {
    buttonColors,
    buttonStyle,
    backgroundColor,
    gradientBackgroundColours,
    iconOrTextColor,
  };
};

// utility function to convert hex color to HSL, adjust it, and convert back to hex
// merci chat gpt
function getSecondaryColor(hex: string, adjustment = 0.4) {
  // default adjustment set to 0.4
  // Convert hex to RGB
  let r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  // Convert RGB to HSL
  (r /= 255), (g /= 255), (b /= 255);
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  // Adjust lightness to make lighter
  l = Math.max(0, Math.min(1, l + adjustment)); // Lighten color

  // Convert HSL back to RGB
  function hslToRgb(h, s, l) {
    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      function hue2rgb(p, q, t) {
        if (t < 0) {
          t += 1;
        }
        if (t > 1) {
          t -= 1;
        }
        if (t < 1 / 6) {
          return p + (q - p) * 6 * t;
        }
        if (t < 1 / 2) {
          return q;
        }
        if (t < 2 / 3) {
          return p + (q - p) * (2 / 3 - t) * 6;
        }
        return p;
      }

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }

  const [finalR, finalG, finalB] = hslToRgb(h, s, l);
  return `#${finalR.toString(16).padStart(2, '0')}${finalG
    .toString(16)
    .padStart(2, '0')}${finalB.toString(16).padStart(2, '0')}`;
}
